/**
 * Maps a typed merge error to user-facing copy. Keeping this pure and separate
 * lets the modal stay declarative and lets tests assert the code → message
 * contract directly.
 */

import type { MergeErrorCode, ProspectResponse } from "../../../../../types/api";
import { MergeError } from "../../../../../services/prospects";
import { getSafeErrorMessage } from "../../../../../utils/errorHandler";
import { prospectName } from "./mergeFields";

/**
 * A subset of error codes for which the UI should re-fetch the player list,
 * because the local snapshot is known to be stale.
 */
export const shouldRefetchAfter = (code: MergeErrorCode): boolean =>
  code === "STALE_READ" || code === "ALREADY_MERGED" || code === "PROSPECT_NOT_FOUND";

/**
 * Produce a specific, human-readable message for a merge failure.
 *
 * When the error carries conflicting ids we name the implicated players (looked
 * up in `prospects` when available, otherwise by id) so the coach knows exactly
 * which profiles to revisit. Unknown / non-merge errors fall back to the shared
 * safe-error formatter.
 */
export const mergeErrorMessage = (
  err: unknown,
  prospects: ProspectResponse[] = []
): string => {
  if (!(err instanceof MergeError)) {
    return getSafeErrorMessage(err, "Failed to merge players");
  }

  const nameFor = (id: string): string => {
    const match = prospects.find((p) => p.id === id);
    return match ? prospectName(match) : id;
  };
  const namedConflicts = err.conflictingIds.map(nameFor).join(", ");

  switch (err.code) {
    case "STALE_READ":
      return "These players changed since you loaded them — refresh and try the merge again.";
    case "ALREADY_MERGED":
      return namedConflicts
        ? `Some of these players were already merged or removed: ${namedConflicts}. Refresh and try again.`
        : "Some of these players were already merged or removed. Refresh and try again.";
    case "FORBIDDEN_ROLE":
      return "Only admins can merge players.";
    case "CROSS_ORG":
      return "One or more selected players belong to a different organization and cannot be merged.";
    case "PROSPECT_NOT_FOUND":
      return namedConflicts
        ? `Some players could not be found: ${namedConflicts}. Refresh and try again.`
        : "One or more selected players could not be found. Refresh and try again.";
    case "SELF_MERGE":
      return "A player cannot be merged into itself. Pick a different survivor.";
    case "EMPTY_LOSERS":
      return "Select at least two players to merge.";
    case "REQUIRED_FIELD_BLANK":
      return "First and last name are required on the surviving profile — choose non-empty values.";
    case "INVALID_FIELD":
      return "One of the resolved field values is invalid. Review your choices and try again.";
    case "IDEMPOTENCY_MISMATCH":
      return "This merge was already attempted with different details. Refresh and start over.";
    default:
      return err.message || "Failed to merge players.";
  }
};
